"use client";

import React from "react";
import { ArrowRight, Check, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function JoinTheMovement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="20163" id="free-drill-drop" ref={ref} className={cn("bg-[#feeaf1] text-primary py-24 px-4 relative overflow-hidden", "flex flex-col items-center justify-center min-h-[60vh]")}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjMDAwIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz4KPC9zdmc+')] [mask-image:linear-gradient(to_bottom,transparent,black)]" />

      <div className={cn("w-full max-w-4xl mx-auto text-center z-10 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <h2 className="text-4xl md:text-6xl lg:text-[4.5rem] font-black uppercase tracking-tighter mb-12 leading-[0.9] text-primary">Stay up to date</h2>

        <div className="w-full max-w-xl mx-auto">
          <FormProvider formKey="free_drill_drop_home" formId="free_drill_drop_home" sectionName="free-drill-drop" defaultValues={{
          name: "",
          email: "",
          consent: false
        }} validationRules={{
          name: {
            required: false
          },
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="Subscribe" successMessage="Welcome to the circle. Check your inbox." errorMessage="Something went wrong. Please try again.">
            <NewsletterForm />
          </FormProvider>
        </div>

        <p className="mt-12 text-primary/60 text-sm uppercase font-bold tracking-widest opacity-80">Join our newsletter!</p>
      </div>
    </section>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-8 px-6 border border-primary/10 bg-white/80 backdrop-blur-sm rounded-lg animate-in fade-in zoom-in duration-500 shadow-lg">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-12 w-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
            <Check className="h-6 w-6" />
          </div>
          <p className="text-xl font-bold tracking-tight text-primary">{successMessage}</p>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8 text-left">
      <div className="space-y-6">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormControl>
                <Input placeholder="ATHLETE NAME (OPTIONAL)" {...field} className="border-0 border-b-2 border-primary/20 bg-white rounded-none px-4 py-6 text-lg md:text-xl placeholder:text-primary/40 focus-visible:ring-0 focus-visible:border-primary transition-colors shadow-sm" />
              </FormControl>
              <FormMessage className="mt-2" />
            </FormItem>} />

        {}
        <div className="relative">
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-0">
                <FormControl>
                  <Input type="email" placeholder="EMAIL ADDRESS" {...field} className="border-0 border-b-2 border-primary/20 bg-white rounded-none px-4 py-6 text-lg md:text-xl placeholder:text-primary/40 focus-visible:ring-0 focus-visible:border-primary transition-colors pr-12 shadow-sm" />
                </FormControl>
                <FormMessage className="mt-2" />
              </FormItem>} />
          
          {}
          <Button type="submit" disabled={isSubmitting} size="icon" variant="ghost" className="absolute right-2 top-2 hover:bg-transparent text-primary hover:text-primary/70 transition-all group">
            {isSubmitting ? <Loader className="h-6 w-6 animate-spin" /> : <ArrowRight className="h-8 w-8 transition-transform duration-300 group-hover:translate-x-2" />}
            <span className="sr-only">Subscribe</span>
          </Button>
        </div>

        {}
        <FormField control={control} name="consent" rules={{
        required: "You must agree to receive updates"
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
              <FormControl>
                <div className="relative flex items-center">
                  <input type="checkbox" className="peer h-4 w-4 shrink-0 appearance-none border border-primary/30 bg-white checked:bg-primary checked:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all" checked={field.value} onChange={field.onChange} id="consent-checkbox" />
                  <Check className="absolute left-0 top-0 h-4 w-4 hidden peer-checked:block text-white pointer-events-none" />
                </div>
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel htmlFor="consent-checkbox" className="text-xs text-primary/70 font-bold uppercase tracking-wide cursor-pointer hover:text-primary transition-colors">
                  I agree to receive softball drills, coaching tips, and updates from CJ’s Circle.
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center font-bold">
          {submitError}
        </div>}
    </Form>;
}